/**
 * API client for the Bookmark Intelligence API (v1).
 *
 * All endpoints return the envelope { data, meta, error } (AGENTS.md Rule 3).
 * Non-2xx statuses throw ApiError with the server-provided error code —
 * pages catch it and map codes to localized messages.
 *
 * Cloudflare Access handles auth via cookies — no credentials handling needed.
 */

import type {
  ApiError as ApiErrorType,
  ApiMeta,
  ApiResponse,
  AuditLogEntry,
  Bookmark,
  BookmarkCreate,
  BookmarkUpdate,
  DedupResolveAction,
  DedupResolveResponse,
  DedupUnresolveMember,
  DuplicateGroup,
  FeatureFlags,
  ImportBatch,
  Tag,
  TagCreate,
  TagMergeResponse,
  TagUpdate,
} from './types'

const API_BASE = '/api/v1'

export interface ApiErrorBody extends ApiErrorType {
  status: number
}

export class ApiError extends Error {
  code: string
  status: number
  details: Record<string, unknown> | undefined

  constructor(body: ApiErrorBody) {
    super(body.message)
    this.name = 'ApiError'
    this.code = body.code
    this.status = body.status
    this.details = body.details
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  query?: Record<string, string | string[] | number | undefined>
  body?: unknown
  rawBody?: BodyInit
}

function buildQuery(query?: RequestOptions['query']): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue
    if (Array.isArray(value)) {
      // Multi-value params use repeated syntax: ?tag=a&tag=b (AGENTS.md Rule 3)
      for (const item of value) params.append(key, String(item))
    } else {
      params.append(key, String(value))
    }
  }
  const str = params.toString()
  return str ? `?${str}` : ''
}

async function requestEnvelope<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiResponse<T>> {
  const { method = 'GET', query, body, rawBody } = options

  const init: RequestInit = {
    method,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined && { 'Content-Type': 'application/json' }),
    },
    body: body !== undefined ? JSON.stringify(body) : (rawBody ?? null),
  }

  const res = await fetch(`${API_BASE}${path}${buildQuery(query)}`, init)

  let envelope: ApiResponse<T> | null = null
  try {
    envelope = (await res.json()) as ApiResponse<T>
  } catch {
    // Non-JSON response — treat as server error
  }

  if (!res.ok || !envelope) {
    const errorBody: ApiErrorBody =
      envelope?.error && typeof envelope.error === 'object'
        ? { ...envelope.error, status: res.status }
        : {
            code: 'INTERNAL_ERROR',
            message: `Request failed with status ${res.status}`,
            status: res.status,
          }
    throw new ApiError(errorBody)
  }

  return envelope
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const envelope = await requestEnvelope<T>(path, options)
  return envelope.data as T
}

async function requestList<T>(path: string, options: RequestOptions = {}): Promise<{ items: T[]; nextCursor: string | null; meta: ApiMeta }> {
  const envelope = await requestEnvelope<T[]>(path, options)
  return {
    items: envelope.data ?? [],
    nextCursor: envelope.meta.next_cursor ?? null,
    meta: envelope.meta,
  }
}

// ============================================================================
// Bookmarks
// ============================================================================

export interface BookmarkListParams {
  q?: string
  status?: string[]
  purpose?: string[]
  sensitivity?: string[]
  source?: string[]
  tag?: string[]
  import_batch_id?: string
  folder?: string
  include_subfolders?: boolean
  sort?: 'created_at_desc' | 'created_at_asc' | 'title_asc' | 'relevance'
  cursor?: string
  limit?: number
}

export async function listBookmarks(
  params: BookmarkListParams,
): Promise<{ items: Bookmark[]; nextCursor: string | null; meta: ApiMeta }> {
  return requestList<Bookmark>('/bookmarks', {
    query: {
      q: params.q,
      status: params.status,
      purpose: params.purpose,
      sensitivity: params.sensitivity,
      source: params.source,
      tag: params.tag,
      import_batch_id: params.import_batch_id,
      folder: params.folder,
      include_subfolders: params.include_subfolders ? 'true' : undefined,
      sort: params.sort,
      cursor: params.cursor,
      limit: params.limit,
    },
  })
}

export interface FolderStat {
  path: string
  count: number
}

export async function listFolderStats(): Promise<FolderStat[]> {
  return request<FolderStat[]>('/bookmarks/folders')
}

export interface RenameFolderResult {
  from: string
  to: string
  affected_count: number
}

/**
 * Rename a folder (and its subtree) or merge it into an existing folder
 * by renaming to the target path (folder-navigation spec §4.3).
 */
export async function renameFolder(from: string, to: string): Promise<RenameFolderResult> {
  return request<RenameFolderResult>('/bookmarks/folders/rename', {
    method: 'POST',
    body: { from, to },
  })
}

export async function getBookmark(id: string): Promise<Bookmark> {
  return request<Bookmark>(`/bookmarks/${encodeURIComponent(id)}`)
}

export async function createBookmark(input: BookmarkCreate): Promise<Bookmark> {
  return request<Bookmark>('/bookmarks', { method: 'POST', body: input })
}

export async function createBookmarkBulk(
  bookmarkIds: string[],
  action: string,
  params?: Record<string, unknown>,
): Promise<{ affected_count: number; skipped_count: number; skipped_ids: string[]; errors: unknown[] }> {
  return request('/bookmarks/bulk', {
    method: 'POST',
    body: { bookmark_ids: bookmarkIds, action, ...(params ? { params } : {}) },
  })
}

export async function updateBookmark(id: string, patch: BookmarkUpdate): Promise<Bookmark> {
  return request<Bookmark>(`/bookmarks/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: patch,
  })
}

export async function setBookmarkFolder(id: string, folderPath: string | null): Promise<Bookmark> {
  return updateBookmark(id, { folder_path: folderPath })
}

export async function replaceBookmarkTags(id: string, tagIds: string[]): Promise<void> {
  await request<void>(`/bookmarks/${encodeURIComponent(id)}/tags`, {
    method: 'POST',
    body: { tag_ids: tagIds },
  })
}

// ============================================================================
// AI classification (Phase 3)
// ============================================================================

export interface AiSuggestion {
  purpose: string
  topicTags: string[]
  newTagSuggestions: string[]
  confidence: number | null
  reasoning: string | null
}

export interface ClassifyResult {
  id: string
  suggestion: AiSuggestion
  routing: { tier: 'cloud' | 'private'; fallback: boolean; reason: string }
  page_fetched: boolean
  model: string
}

export async function classifyBookmark(id: string, lang?: 'en' | 'ru'): Promise<ClassifyResult> {
  return request<ClassifyResult>(`/classify/bookmark/${encodeURIComponent(id)}${lang ? `?lang=${lang}` : ''}`, {
    method: 'POST',
    body: {},
  })
}

export async function applyAiSuggestion(
  id: string,
  options: { apply_purpose?: boolean; apply_tags?: boolean } = {},
): Promise<Bookmark> {
  return request<Bookmark>(`/bookmarks/${encodeURIComponent(id)}/apply-ai-suggestion`, {
    method: 'POST',
    body: options,
  })
}

export async function rejectAiSuggestion(id: string): Promise<{ id: string; rejected: boolean }> {
  return request(`/bookmarks/${encodeURIComponent(id)}/reject-ai-suggestion`, {
    method: 'POST',
    body: {},
  })
}

export interface ClassifyJobProgress {
  id: string
  status: 'running' | 'paused' | 'completed' | 'failed'
  total: number
  processed: number
  accepted: number
  rejected_validation: number
  cloud: number
  private: number
  errors: number
}

export async function startClassifyBatch(bookmarkIds: string[], lang?: 'en' | 'ru'): Promise<{ job_id: string; status: string; total: number }> {
  return request('/classify/batch', {
    method: 'POST',
    body: { bookmark_ids: bookmarkIds, ...(lang ? { lang } : {}) },
  })
}

export async function getClassifyJob(jobId: string): Promise<ClassifyJobProgress> {
  return request<ClassifyJobProgress>(`/classify/jobs/${encodeURIComponent(jobId)}`)
}

export async function continueClassifyJob(
  jobId: string,
  lang?: 'en' | 'ru',
): Promise<{ job_id: string; status: string; remaining: number; total: number }> {
  return request(`/classify/jobs/${encodeURIComponent(jobId)}/continue`, {
    method: 'POST',
    body: { ...(lang ? { lang } : {}) },
  })
}

// ============================================================================
// Tag suggestions (Phase 3 — предложенные ИИ теги)
// ============================================================================

export interface TagSuggestion {
  id: string
  slug: string
  label: string
  bookmark_id: string
  confidence: number | null
  status: 'pending' | 'approved' | 'rejected'
  created_at: string
  bookmark: { id: string; title: string | null; url_normalized: string } | null
}

export async function listTagSuggestions(
  status: 'pending' | 'approved' | 'rejected' = 'pending',
  limit = 100,
  bookmarkId?: string,
): Promise<TagSuggestion[]> {
  return request<TagSuggestion[]>(`/tag-suggestions`, {
    query: { status, limit, ...(bookmarkId ? { bookmark_id: bookmarkId } : {}) },
  })
}

export async function approveTagSuggestion(
  id: string,
  options: { merge_into_tag_id?: string; label?: string } = {},
): Promise<{ id: string; status: string; tag_id?: string; merged_into_tag_id?: string }> {
  return request(`/tag-suggestions/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    body: options,
  })
}

export async function rejectTagSuggestion(id: string): Promise<{ id: string; status: string }> {
  return request(`/tag-suggestions/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    body: {},
  })
}

// ============================================================================
// Duplicates
// ============================================================================

export async function listDuplicateGroups(
  cursor?: string,
  limit = 20,
): Promise<{ items: DuplicateGroup[]; nextCursor: string | null }> {
  return requestList<DuplicateGroup>('/bookmarks/duplicates', {
    query: { cursor, limit },
  })
}

export interface ResolveDuplicateInput {
  action: DedupResolveAction
  canonical_bookmark_id?: string
}

export async function resolveDuplicateGroup(
  groupId: string,
  input: ResolveDuplicateInput,
): Promise<DedupResolveResponse> {
  return request(`/bookmarks/duplicates/${encodeURIComponent(groupId)}/resolve`, {
    method: 'POST',
    body: input,
  })
}

export interface UnresolveDuplicateInput {
  members: DedupUnresolveMember[]
  canonical_bookmark_id?: string | null
  transferred_tags?: string[]
}

export async function unresolveDuplicateGroup(
  groupId: string,
  input: UnresolveDuplicateInput,
): Promise<{ group_id: string; restored_count: number; expected_count: number }> {
  return request(`/bookmarks/duplicates/${encodeURIComponent(groupId)}/unresolve`, {
    method: 'POST',
    body: input,
  })
}

// ============================================================================
// Import
// ============================================================================

export interface ImportResponse {
  import_batch_id: string
  status: ImportBatch['status']
}

export async function importBookmarksFile(
  file: File,
  sourceHint?: 'safari' | 'edge',
): Promise<ImportResponse> {
  const form = new FormData()
  form.append('file', file)
  if (sourceHint) form.append('source_hint', sourceHint)
  const envelope = await requestEnvelope<ImportResponse>('/import', {
    method: 'POST',
    rawBody: form,
  })
  return envelope.data as ImportResponse
}

export async function getImportHistory(limit = 20): Promise<ImportBatch[]> {
  return request<ImportBatch[]>('/import', { query: { limit } })
}

export async function getImportBatch(batchId: string): Promise<ImportBatch> {
  return request<ImportBatch>(`/import/${encodeURIComponent(batchId)}`)
}

export async function getNeedsDecision(batchId: string, limit = 200): Promise<Bookmark[]> {
  return request<Bookmark[]>(`/import/${encodeURIComponent(batchId)}/needs-decision`, {
    query: { limit },
  })
}

// ============================================================================
// Export
// ============================================================================

export async function exportBookmarks(
  scope: 'all' | 'selection' = 'all',
  bookmarkIds: string[] = [],
): Promise<string> {
  const res = await fetch(
    `${API_BASE}/export${buildQuery({
      scope,
      bookmark_ids: bookmarkIds,
      format: 'html',
    })}`,
  )
  if (!res.ok) {
    let message = `Export failed with status ${res.status}`
    try {
      const env = (await res.json()) as ApiResponse<unknown>
      if (env?.error?.message) message = env.error.message
    } catch {
      // keep default message
    }
    throw new ApiError({ code: 'INTERNAL_ERROR', message, status: res.status })
  }
  return res.text()
}

/** Полный JSON-бэкап (теги, purpose, status, notes, папки) — Block 4. */
export async function exportBackupJson(scope: 'all' | 'selection' = 'all', bookmarkIds: string[] = []): Promise<Blob> {
  const res = await fetch(
    `${API_BASE}/export${buildQuery({
      scope,
      bookmark_ids: bookmarkIds,
      format: 'json',
    })}`,
  )
  if (!res.ok) {
    let message = `Export failed with status ${res.status}`
    try {
      const env = (await res.json()) as ApiResponse<unknown>
      if (env?.error?.message) message = env.error.message
    } catch {
      // keep default message
    }
    throw new ApiError({ code: 'INTERNAL_ERROR', message, status: res.status })
  }
  return res.blob()
}

// ============================================================================
// Tags
// ============================================================================

export interface TagListParams {
  facet?: 'topic' | 'project'
  status?: 'approved' | 'pending' | 'deprecated'
  includeDeprecated?: boolean
  limit?: number
}

export async function listTags(params: TagListParams = {}): Promise<Tag[]> {
  return request<Tag[]>('/tags', {
    query: {
      facet: params.facet,
      status: params.status,
      include_deprecated: params.includeDeprecated ? 'true' : undefined,
      limit: params.limit ?? 200,
    },
  })
}

export async function createTag(input: TagCreate): Promise<Tag> {
  return request<Tag>('/tags', { method: 'POST', body: input })
}

export async function updateTag(id: string, patch: TagUpdate): Promise<Tag> {
  return request<Tag>(`/tags/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch })
}

export async function mergeTagInto(sourceId: string, targetId: string): Promise<TagMergeResponse> {
  return request<TagMergeResponse>(`/tags/${encodeURIComponent(sourceId)}/merge-into`, {
    method: 'POST',
    body: { target_tag_id: targetId },
  })
}

export interface UnmergeTagInput {
  target_tag_id: string
  moved_bookmark_ids: string[]
  moved_suggestion_ids: string[]
}

export async function unmergeTag(sourceId: string, input: UnmergeTagInput) {
  return request(`/tags/${encodeURIComponent(sourceId)}/unmerge`, {
    method: 'POST',
    body: input,
  })
}

export interface TagDeleteResponse {
  deleted_tag_id: string
  removed_bookmark_links: number
}

export async function deleteTag(id: string): Promise<TagDeleteResponse> {
  return request<TagDeleteResponse>(`/tags/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

// ============================================================================
// Audit
// ============================================================================

export interface AuditListParams {
  actor?: string
  action?: string
  cursor?: string
  limit?: number
}

export async function listAuditEntries(
  params: AuditListParams = {},
): Promise<{ items: AuditLogEntry[]; nextCursor: string | null }> {
  return requestList<AuditLogEntry>('/audit', {
    query: {
      actor: params.actor,
      action: params.action,
      cursor: params.cursor,
      limit: params.limit,
    },
  })
}

// ============================================================================
// Feature flags
// ============================================================================

export async function getFeatureFlags(): Promise<FeatureFlags> {
  return request<FeatureFlags>('/feature-flags')
}

/** PATCH /feature-flags — только allow-list (ai_auto_apply_tags), остальное 400. */
export async function updateFeatureFlag(
  key: string,
  enabled: boolean,
): Promise<{ key: string; enabled: boolean }> {
  return request('/feature-flags', { method: 'PATCH', body: { key, enabled } })
}

// ============================================================================
// AI provider settings (Phase 3)
// ============================================================================

export interface AiProviderConfig {
  baseUrl: string
  model: string
  apiKeyConfigured: boolean
}

export interface AiSettings {
  cloud: AiProviderConfig
  private: AiProviderConfig
  behavior: {
    suggest_new_tags: boolean
  }
}

export interface AiTestResult {
  tier: 'cloud' | 'private'
  ok: boolean
  latency_ms: number
  model: string
  response: string
}

export async function getAiSettings(): Promise<AiSettings> {
  return request<AiSettings>('/settings/ai')
}

/** PATCH /settings/ai без tier — переключение поведения (suggest_new_tags). */
export async function updateAiBehavior(patch: { suggest_new_tags: boolean }): Promise<{
  behavior: { suggest_new_tags: boolean }
}> {
  return request('/settings/ai', { method: 'PATCH', body: patch })
}

export async function updateAiProvider(
  tier: 'cloud' | 'private',
  baseUrl: string,
  model: string,
): Promise<AiProviderConfig> {
  return request<AiProviderConfig>(`/settings/ai`, {
    method: 'PATCH',
    body: { tier, base_url: baseUrl, model },
  })
}

export async function testAiProvider(tier: 'cloud' | 'private'): Promise<AiTestResult> {
  return request<AiTestResult>('/settings/ai', { method: 'POST', body: { tier } })
}

// ============================================================================
// Data maintenance
// ============================================================================

export async function clearAllBookmarks(): Promise<{ deleted_count: number }> {
  return request<{ deleted_count: number }>('/bookmarks/clear-all', {
    method: 'POST',
    body: { confirmation: 'CLEAR ALL' },
  })
}

// ============================================================================
// Re-export for convenience
// ============================================================================

export type { ApiErrorType, ApiMeta }
